import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Spin, Typography, message } from 'antd';
import { apiService } from '../../services/api';
import { TermsAndConditions } from '../terms-and-conditions';
import { TERMS_VERSION, hasAcceptedCurrentTerms } from '../../legal/terms';

const { Title, Paragraph } = Typography;

/**
 * Full-page terms acceptance for every signed-in tutor whose profile
 * does not record the current terms version. Shown before the app shell.
 */
export const TermsGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService.getProfile()
      .then((response) => {
        const profile = (response.data as any)?.profile || response.data;
        if (!cancelled) setAccepted(hasAcceptedCurrentTerms(profile));
      })
      .catch(() => {
        if (!cancelled) setAccepted(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (accepted) return <>{children}</>;

  const continueEnabled = termsChecked && privacyChecked && !saving;

  const onContinue = async () => {
    setSaving(true);
    try {
      await apiService.acceptTerms(TERMS_VERSION);
      setAccepted(true);
    } catch (error) {
      console.error('Failed to record terms acceptance', error);
      message.error('Could not save your acceptance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '32px 16px 48px' }}>
      <div style={{ maxWidth: 840, margin: '0 auto' }}>
        <Title level={3} style={{ textAlign: 'center' }}>Before you continue</Title>
        <Paragraph style={{ textAlign: 'center' }}>
          Please read and accept the StageNote terms and privacy policy. Version {TERMS_VERSION}.
        </Paragraph>
        <div style={{ maxHeight: '55vh', overflow: 'auto', marginBottom: 24, background: '#fff', borderRadius: 8 }}>
          <TermsAndConditions />
        </div>
        <div style={{ background: '#fff', borderRadius: 8, padding: 24 }}>
          <Paragraph>
            StageNote is run by Intecco Pty Ltd (ABN 53 161 240 580). Your account and the
            student, parent, and lesson records you add are stored in Sydney, Australia.
            You decide what to store about your students. We process those records only to
            run your account. We do not sell them. If you teach a child in the EU or EEA,
            you need a lawful reason to keep their details. Where the child is below the
            age set in that country, from 13 to 16, you need consent from the person with
            parental responsibility unless another lawful basis applies. The{' '}
            <a href="/privacy-policy.html" target="_blank" rel="noreferrer">full privacy policy</a>
            {' '}lists the data protection authority for each EU and EEA country.
            Privacy questions go to hello@stagenote.ai.
          </Paragraph>
          <Checkbox checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)}>
            I accept the Terms of Service (version {TERMS_VERSION})
          </Checkbox>
          <div style={{ height: 12 }} />
          <Checkbox checked={privacyChecked} onChange={(e) => setPrivacyChecked(e.target.checked)}>
            I accept the Privacy Policy (version {TERMS_VERSION}), including that StageNote processes student and parent records only on my instructions
          </Checkbox>
          <div style={{ marginTop: 24 }}>
            <Button type="primary" disabled={!continueEnabled} loading={saving} onClick={onContinue}>
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
